/**
 * M2 (enxugamento) — "Fonte única": a evolução do Prontuário é COMPOSTA
 * deterministicamente dos módulos estruturados do atendimento (queixa,
 * exames, hipótese/CID) + narrativa da extração (HMA/exame físico) quando
 * houver + texto da Conduta. Fim da digitação dupla texto-livre × módulos.
 *
 * Valores medidos saem EXATOS via `serializeEncounterExams` — a IA nunca
 * parafraseia medida (mesma regra dos laudos).
 */

import { serializeEncounterExams } from '@/utils/encounterSerialize';
import type { EncounterExamsState, EncounterTypeDef } from '@/types/encounter';
import type { AIEvolucao } from '@/types/ai';
import type { ExamFillMap } from '@/utils/encounterAutoFill';

/**
 * Módulos que ganham seção própria na evolução gerada (e por isso são
 * EXCLUÍDOS da serialização de exames — senão sairiam duplicados).
 */
export const EVOLUCAO_SECTION_MODULE_IDS: readonly string[] = [
  'queixa-principal',
  'historia-doenca-atual',
  'antecedentes-pessoais',
  'antecedentes-familiares',
  'diagnostico',
  'cid',
  'conduta',
];

/** Valor `mono` de um campo de módulo, trim-ado ('' quando ausente). */
function getMono(state: EncounterExamsState, moduleId: string, fieldKey: string): string {
  const entry = state.entries.find((e) => e.moduleId === moduleId);
  return entry?.values[fieldKey]?.mono?.trim() ?? '';
}

export interface ComposeEvolucaoOptions {
  /** Tipos de atendimento — só pra resolver o label do `state.typeId`. */
  encounterTypes?: EncounterTypeDef[];
  /** Narrativa da extração (HMA) — entra como seção própria quando houver. */
  historiaAtual?: string;
  /** Narrativa da extração (exame físico descritivo). */
  exameFisico?: string;
  /** Texto da Conduta (drafts.conduta) — a conduta NÃO é módulo (fluxo
   *  próprio com data de retorno → portal), só entra no texto gerado. */
  conduta?: string;
}

/**
 * Compõe a evolução em Markdown. Só inclui seções com conteúdo; retorna ''
 * quando não há nada estruturado nem narrativa (o chamador decide fallback).
 */
export function composeEvolucao(
  state: EncounterExamsState,
  options: ComposeEvolucaoOptions = {},
): string {
  const queixa = getMono(state, 'queixa-principal', 'queixa');
  const antecedentesPessoais = getMono(state, 'antecedentes-pessoais', 'antecedentes');
  const antecedentesFamiliares = getMono(state, 'antecedentes-familiares', 'antecedentes');
  const hipoteses = getMono(state, 'diagnostico', 'diagnostico');
  const cid = getMono(state, 'cid', 'cid');
  // HDA: o módulo manda (o médico pode ter editado); a narrativa da extração
  // só entra quando o módulo está vazio.
  const historiaAtual =
    getMono(state, 'historia-doenca-atual', 'historia') || (options.historiaAtual?.trim() ?? '');
  const exameFisico = options.exameFisico?.trim() ?? '';
  // Conduta pode vir do módulo (novo) e/ou do Documento de Texto (drafts) —
  // soma os dois na mesma seção, sem repetir texto idêntico.
  const condutaModulo = getMono(state, 'conduta', 'conduta');
  const condutaDraft = options.conduta?.trim() ?? '';
  const conduta = [...new Set([condutaModulo, condutaDraft].filter(Boolean))].join('\n');

  const sectioned = new Set(EVOLUCAO_SECTION_MODULE_IDS);
  const examOnly: EncounterExamsState = {
    ...state,
    entries: state.entries.filter((e) => !sectioned.has(e.moduleId)),
  };
  // Agrupado por OLHO: a leitura clínica é "OD esf/cil/eixo", não campo a
  // campo com os dois olhos dentro de cada um.
  const exames = serializeEncounterExams(examOnly, options.encounterTypes, {
    groupByEye: true,
  });

  const parts: string[] = [];
  if (queixa) parts.push(`## Queixa Principal\n${queixa}`);
  if (historiaAtual) parts.push(`## História da Moléstia Atual\n${historiaAtual}`);
  if (antecedentesPessoais) parts.push(`## Antecedentes Pessoais\n${antecedentesPessoais}`);
  if (antecedentesFamiliares) parts.push(`## Antecedentes Familiares\n${antecedentesFamiliares}`);
  if (exames) parts.push(exames);
  if (exameFisico) parts.push(`## Exame Físico\n${exameFisico}`);
  if (hipoteses || cid) {
    const lines: string[] = [];
    if (hipoteses) lines.push(hipoteses);
    if (cid) lines.push(`CID-10: ${cid}`);
    parts.push(`## Hipóteses Diagnósticas\n${lines.join('\n')}`);
  }
  if (conduta) parts.push(`## Conduta\n${conduta}`);

  return parts.join('\n\n').trim();
}

/**
 * Converte a evolução estruturada da extração (`AIEvolucao`) num
 * `ExamFillMap` pros módulos de anamnese/diagnóstico — entra pelo MESMO
 * caminho não-destrutivo do "Preencher com IA" (`applyExamFill` com
 * `overwrite: false`): o que o médico já digitou nos módulos prevalece.
 */
export function buildEvolucaoModuleFill(ev: AIEvolucao | undefined | null): ExamFillMap {
  if (!ev) return {};
  const fill: ExamFillMap = {};

  const queixa = ev.queixaPrincipal?.trim();
  if (queixa) fill['queixa-principal'] = { queixa: { mono: queixa } };

  const historia = ev.historiaAtual?.trim();
  if (historia) fill['historia-doenca-atual'] = { historia: { mono: historia } };

  const antecedentesPessoais = ev.antecedentesPessoais?.trim();
  if (antecedentesPessoais) {
    fill['antecedentes-pessoais'] = { antecedentes: { mono: antecedentesPessoais } };
  }

  const antecedentesFamiliares = ev.antecedentesFamiliares?.trim();
  if (antecedentesFamiliares) {
    fill['antecedentes-familiares'] = { antecedentes: { mono: antecedentesFamiliares } };
  }

  const hipoteses = (ev.hipotesesDiagnosticas ?? [])
    .map((h) => h.trim())
    .filter(Boolean);
  if (hipoteses.length > 0) {
    fill['diagnostico'] = { diagnostico: { mono: hipoteses.join('\n') } };
  }

  const cid = ev.cid?.trim();
  if (cid) fill['cid'] = { cid: { mono: cid } };

  return fill;
}
